import { useEffect, useMemo, useState } from "react";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";
import { money } from "../../lib/format";
import { reviewStatusLabel } from "../../lib/adminDisplayLabels";

interface MemberSummary {
  member: null | {
    member_account: string;
    status: string;
    status_label: string;
  };
}

interface RescueReview {
  id: number;
  status: string;
  payload_json?: string | null;
  applicant_note?: string | null;
  review_note?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  created_at?: string;
  reviewed_at?: string | null;
}

interface RescueStatus {
  status: "not_submitted" | "submitted";
  apply_month: string;
  is_eligible: boolean;
  member_status?: string | null;
  review_status?: string | null;
  review_type?: string | null;
  reason?: string | null;
  applicant_note?: string | null;
  review_note?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  reviewed_at?: string | null;
  created_at?: string | null;
  reward_status?: string | null;
  reward_type?: string | null;
  reward_amount?: number | null;
  reward_completed_at?: string | null;
  display_status?: string | null;
  display_message?: string | null;
  review_request: RescueReview | null;
}

interface RescueApplyResult {
  created: boolean;
  review_request: RescueReview;
}

export function RescuePage() {
  const account = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";
  const defaultMonth = new Date().toISOString().slice(0, 7);
  const [summary, setSummary] = useState<MemberSummary | null>(null);
  const [rescueStatus, setRescueStatus] = useState<RescueStatus | null>(null);
  const [applyMonth, setApplyMonth] = useState(defaultMonth);
  const [reason, setReason] = useState("");
  const [applicantNote, setApplicantNote] = useState("");
  const [agreeReview, setAgreeReview] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load(month = applyMonth) {
    if (!account) return;
    const [memberData, rescueData] = await Promise.all([
      api<MemberSummary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`),
      api<RescueStatus>(`/api/public/rescue-bonus?member_account=${encodeURIComponent(account)}&apply_month=${encodeURIComponent(month)}`)
    ]);
    setSummary(memberData);
    setRescueStatus(rescueData);
    const payload = parseRescuePayload(rescueData.review_request?.payload_json);
    setReason(payload?.reason ?? rescueData.reason ?? "");
    setApplicantNote(payload?.applicant_note ?? rescueData.applicant_note ?? "");
    setAgreeReview(Boolean(payload?.agree_review));
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : "回穩補給金資料讀取失敗"));
  }, [account]);

  const currentReview = rescueStatus?.review_request ?? null;
  const alreadySubmitted = Boolean(currentReview);
  const canSubmit = Boolean(rescueStatus?.is_eligible && !alreadySubmitted);
  const statusInfo = useMemo(() => rescueFrontendStatus(rescueStatus), [rescueStatus]);

  async function submitRescue() {
    setMessage("");
    setError("");
    try {
      if (!account) throw new Error("請先登入龍來喬活動大廳。");
      if (!canSubmit) throw new Error("目前不可送出回穩補給金申請。");
      if (!reason.trim()) throw new Error("請填寫申請原因。");
      if (!agreeReview) throw new Error("請同意官方審核存款 / 投注 / 輸贏 / 風控資料。");
      const result = await api<RescueApplyResult>("/api/public/rescue-bonus", {
        method: "POST",
        body: JSON.stringify({
          member_account: account,
          apply_month: applyMonth,
          reason: reason.trim(),
          applicant_note: applicantNote.trim(),
          agree_review: true
        })
      });
      setRescueStatus({
        status: "submitted",
        apply_month: applyMonth,
        is_eligible: true,
        member_status: summary?.member?.status,
        review_status: result.review_request.status,
        review_type: "rescue_bonus",
        reason: reason.trim(),
        applicant_note: applicantNote.trim(),
        review_note: result.review_request.review_note,
        reject_reason: result.review_request.reject_reason,
        cancel_reason: result.review_request.cancel_reason,
        reviewed_at: result.review_request.reviewed_at,
        created_at: result.review_request.created_at,
        review_request: result.review_request
      });
      setMessage(result.created ? "已送出回穩補給金申請，等待後台人工確認。" : `本月已有申請紀錄，狀態：${reviewStatusLabel(result.review_request.status)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "回穩補給金申請送出失敗");
    }
  }

  return (
    <PublicLayout title="龍來喬回穩補給金" subtitle="人工審核後才會進入後續派發流程">
      {summary?.member ? (
        <>
          <section className="profile-panel llc-card-gradient">
            <span className="hero-kicker">回穩補給金</span>
            <h2>龍來喬回穩補給金</h2>
            <p>完成新人七關後，正式成員可依本月活躍、遊戲狀況與風控紀錄申請回穩補給金。申請送出後，將由後台人工審核。</p>
            <div className="profile-list">
              <div><dt>會員帳號</dt><dd>{summary.member.member_account}</dd></div>
              <div><dt>會員狀態</dt><dd>{summary.member.status_label}</dd></div>
              <div><dt>申請月份</dt><dd>{rescueStatus?.apply_month ?? applyMonth}</dd></div>
              <div><dt>目前狀態</dt><dd>{statusInfo.title}</dd></div>
            </div>
          </section>

          <section className="profile-panel">
            <span className="hero-kicker">申請資料</span>
            <h2>回穩補給金申請</h2>
            <p>前台不顯示完整淨負級距，也不會自動建立派發。官方會人工核對資料後再判斷。</p>

            <div className={`birthday-status-card ${statusInfo.tone}`}>
              <strong>{statusInfo.title}</strong>
              <span>{statusInfo.description}</span>
              {statusInfo.reason ? <span>原因：{statusInfo.reason}</span> : null}
              {rescueStatus?.reward_amount ? <span>派發金額：{money(rescueStatus.reward_amount)}</span> : null}
              {currentReview?.review_note ? <span>審核備註：{currentReview.review_note}</span> : null}
              {reason && alreadySubmitted ? <span>申請原因：{reason}</span> : null}
              {applicantNote && alreadySubmitted ? <span>申請備註：{applicantNote}</span> : null}
            </div>

            {canSubmit ? (
              <>
                <div className="rescue-form-grid">
                  <label>
                    申請月份
                    <input type="month" value={applyMonth} onChange={(event) => {
                      setApplyMonth(event.target.value);
                      load(event.target.value).catch((err) => setError(err instanceof Error ? err.message : "回穩補給金資料讀取失敗"));
                    }} />
                  </label>
                  <label>
                    申請原因
                    <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="請簡述申請原因" />
                  </label>
                  <label>
                    備註
                    <textarea value={applicantNote} onChange={(event) => setApplicantNote(event.target.value)} placeholder="可補充希望官方審核時參考的資訊" />
                  </label>
                </div>
                <label className="rescue-agreement">
                  <input type="checkbox" checked={agreeReview} onChange={(event) => setAgreeReview(event.target.checked)} />
                  <span>同意官方審核存款 / 投注 / 輸贏 / 風控資料</span>
                </label>
                <button className="primary-cta profile-submit" disabled={!canSubmit} onClick={submitRescue}>送出申請</button>
              </>
            ) : null}
            {message ? <p className="result">{message}</p> : null}
            {error ? <p className="error-box">{error}</p> : null}
          </section>
        </>
      ) : (
        <section className="login-gate">
          <span className="hero-kicker">回穩補給金</span>
          <h1>請先登入龍來喬活動大廳</h1>
          <p>登入後才能查看回穩補給金申請狀態。</p>
          <a className="primary-cta" href="/">前往登入</a>
        </section>
      )}
    </PublicLayout>
  );
}

function rescueFrontendStatus(status: RescueStatus | null) {
  const reviewStatus = status?.review_request?.status ?? status?.review_status;
  const rewardStatus = status?.reward_status;
  if (reviewStatus === "pending") {
    return {
      title: "審核中",
      description: "你的回穩補給金申請已送出，官方正在確認資料。",
      tone: ""
    };
  }
  if (reviewStatus === "approved") {
    return {
      title: "已通過",
      description: "你的回穩補給金申請已通過，等待後續派發安排。",
      tone: ""
    };
  }
  if (reviewStatus === "converted_to_reward") {
    if (rewardStatus === "completed") {
      return {
        title: "已完成派發",
        description: "本月回穩補給金已完成派發。",
        tone: ""
      };
    }
    if (rewardStatus === "ready") {
      return {
        title: "等待人工派發",
        description: "已轉入派發中心，等待人工派發。",
        tone: ""
      };
    }
    if (rewardStatus === "failed") {
      return {
        title: "派發失敗",
        description: "派發失敗，官方將人工處理。",
        tone: "warning"
      };
    }
    if (rewardStatus === "canceled") {
      return {
        title: "派發已取消",
        description: "本月回穩補給金派發已取消。",
        tone: "warning"
      };
    }
    return {
      title: "已轉入派發流程",
      description: "已轉入派發流程，等待官方處理。",
      tone: ""
    };
  }
  if (reviewStatus === "rejected") {
    return {
      title: "本月申請未通過",
      description: "本月回穩補給金申請未通過。如有疑問請洽客服。",
      reason: status?.review_request?.reject_reason ?? status?.reject_reason ?? "",
      tone: "warning"
    };
  }
  if (reviewStatus === "canceled") {
    return {
      title: "本月申請已取消",
      description: "本月回穩補給金申請已取消。",
      reason: status?.review_request?.cancel_reason ?? status?.cancel_reason ?? "",
      tone: "warning"
    };
  }
  if (!status?.is_eligible) {
    return {
      title: "回穩補給金申請尚未開放",
      description: "完成新人七關後，正式成員才可申請龍來喬回穩補給金。如有特殊狀況，請聯繫客服，由後台人工特批。",
      tone: "warning"
    };
  }
  return {
    title: "可申請",
    description: "你可以送出本月回穩補給金申請，官方將人工確認資料。",
    tone: ""
  };
}

function parseRescuePayload(raw?: string | null) {
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== "object") return null;
    return payload as { apply_month?: string; reason?: string; applicant_note?: string; agree_review?: boolean };
  } catch {
    return null;
  }
}
